import {
  forwardRef,
  memo,
  useCallback,
  useEffect,
  useImperativeHandle,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { useEditor, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Collaboration from "@tiptap/extension-collaboration";
import CollaborationCaret from "@tiptap/extension-collaboration-caret";
import type { Doc as YDoc } from "yjs";
import type { Awareness } from "y-protocols/awareness";
import { CommentMark } from "../../extensions/CommentMark";
import { CitationMark } from "../../extensions/CitationMark";
import { ResearchImage } from "../../extensions/ResearchImage";
import { ChapterHighlight } from "../../extensions/ChapterHighlight";
import { ParagraphIds } from "../../extensions/ParagraphIds";
import { ProperNounMark } from "../../extensions/ProperNounMark";
import { SearchExtension } from "../../extensions/SearchExtension";
import type { Chapter } from "../../types";
import {
  applyMarksFromChapterComments,
  getChapterCommentIdAtOverlayPos,
} from "../../utils/commentEditorMarks";
import { syncChapterCommentsAnchorsFromEditor } from "../../utils/commentEditorMarks";
import { serializeChapterContent, chapterContentToPlainText } from "../../../shared/chapterContent";
import {
  chapterContentBodyChanged,
  chapterToTipTapDoc,
  hydrateManuscriptEditor,
  manuscriptJsonFromEditor,
  reconcileManuscriptEditorComments,
} from "../../utils/manuscriptEditorHelpers";
import { createDocumentUndoStack } from "@repo/shared/documentUndoStack";
import { FindReplaceBar } from "./FindReplaceBar";
import { StraightQuotes } from "../../extensions/StraightQuotes";

/** Bold / italic / underline only — no headings, lists, code, strike, fonts, or colors. */
function buildManuscriptExtensions(
  liveCollabYDoc: YDoc | null,
  liveCollabAwareness: Awareness | null,
  liveCollabUser: { name: string; color: string } | null,
  resolveImageSrc?: (assetId: string) => string | null
) {
  return [
    StarterKit.configure({
      heading: false,
      bulletList: false,
      orderedList: false,
      listItem: false,
      blockquote: false,
      codeBlock: false,
      code: false,
      strike: false,
      horizontalRule: false,
      // Collaboration owns history when live collab is on.
      undoRedo: liveCollabYDoc
        ? false
        : {
            depth: 30,
          },
    }),
    ParagraphIds,
    ChapterHighlight,
    CommentMark,
    CitationMark,
    ResearchImage.configure({
      resolveSrc: (assetId) => resolveImageSrc?.(assetId) ?? null,
    }),
    ProperNounMark,
    SearchExtension,
    StraightQuotes,
    ...(liveCollabYDoc
      ? [
          Collaboration.configure({ document: liveCollabYDoc }),
          ...(liveCollabAwareness && liveCollabUser
            ? [
                CollaborationCaret.configure({
                  provider: { awareness: liveCollabAwareness },
                  user: liveCollabUser,
                }),
              ]
            : []),
        ]
      : []),
  ];
}

export type ManuscriptDraftFlushPayload = {
  chapterId: string;
  content: string;
  comments: Chapter["comments"];
};

export type ManuscriptEditorHandle = {
  getEditor: () => Editor | null;
  flushDraft: () => void;
  hasPendingDraft: () => boolean;
  undo: () => boolean;
  redo: () => boolean;
};

type ManuscriptEditorPaneProps = {
  chapterId: string;
  chapterContent: unknown;
  chapterComments: Chapter["comments"];
  editable: boolean;
  themePreset: string | null;
  selectedCommentId: string | null;
  /** Bump when parent replaces chapter text without changing chapter id (library pull, Story Craft apply, etc.). */
  reloadToken: number;
  flushDebounceMs?: number;
  /** Host element for the B/I/U toolbar (ported next to the chapter caption). */
  formatToolbarHost?: HTMLElement | null;
  onCommentSelect: (commentId: string | null) => void;
  onTyping?: () => void;
  onDraftFlush: (payload: ManuscriptDraftFlushPayload) => void;
  /** Open the in-document comment composer for the current selection. */
  onRequestComment?: () => void;
  findReplaceOpen?: boolean;
  onFindReplaceOpenChange?: (open: boolean) => void;
  /** When set, TipTap Collaboration binds to this Y.Doc (Premier live collab). Remount pane when it changes. */
  liveCollabYDoc?: YDoc | null;
  liveCollabAwareness?: Awareness | null;
  liveCollabUser?: { name: string; color: string } | null;
  /** Sync status for live collab chrome (optional). */
  liveCollabStatus?: string | null;
  allowCitations?: boolean;
  allowImages?: boolean;
  citationOptions?: Array<{ id: string; label: string }>;
  imageOptions?: Array<{ id: string; title: string; src?: string }>;
  onInsertCitation?: (citationId: string) => void;
  onInsertImage?: (assetId: string) => void;
  resolveImageSrc?: (assetId: string) => string | null;
};

export const ManuscriptEditorPane = memo(
  forwardRef<ManuscriptEditorHandle, ManuscriptEditorPaneProps>(function ManuscriptEditorPane(
    {
      chapterId,
      chapterContent,
      chapterComments,
      editable,
      themePreset,
      selectedCommentId,
      reloadToken,
      flushDebounceMs = 250,
      formatToolbarHost = null,
      onCommentSelect,
      onTyping,
      onDraftFlush,
      onRequestComment,
      findReplaceOpen: findReplaceOpenProp,
      onFindReplaceOpenChange,
      liveCollabYDoc = null,
      liveCollabAwareness = null,
      liveCollabUser = null,
      liveCollabStatus = null,
      allowCitations = false,
      allowImages = false,
      citationOptions = [],
      imageOptions = [],
      onInsertCitation,
      onInsertImage,
      resolveImageSrc,
    },
    ref
  ) {
    const [findReplaceOpenLocal, setFindReplaceOpenLocal] = useState(false);
    const findReplaceOpen = findReplaceOpenProp ?? findReplaceOpenLocal;
    const setFindReplaceOpen = useCallback(
      (open: boolean) => {
        onFindReplaceOpenChange?.(open);
        if (findReplaceOpenProp === undefined) setFindReplaceOpenLocal(open);
      },
      [findReplaceOpenProp, onFindReplaceOpenChange]
    );
    const appliedCommentMarkIdsRef = useRef<Set<string>>(new Set());
    const syncedChapterIdRef = useRef<string | null>(null);
    /** TipTap instance last hydrated — remounts must re-hydrate even if chapter id is unchanged. */
    const syncedEditorRef = useRef<Editor | null>(null);
    const needsFlushRef = useRef(false);
    const flushTimerRef = useRef<number | null>(null);
    const prevThemeRef = useRef<string | null>(null);
    const editorRef = useRef<Editor | null>(null);
    const chapterIdRef = useRef(chapterId);
    const commentsRef = useRef(chapterComments);
    const storedContentRef = useRef(chapterContent);
    const onDraftFlushRef = useRef(onDraftFlush);
    const onTypingRef = useRef(onTyping);
    const onCommentSelectRef = useRef(onCommentSelect);
    const documentUndoRef = useRef(createDocumentUndoStack());
    const prevReloadTokenRef = useRef(reloadToken);
    const baselineContentRef = useRef("");
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [, setFormatTick] = useState(0);
    const [plainFallback, setPlainFallback] = useState(
      () => chapterContentToPlainText(chapterContent).trim().length > 0
    );
    const editorMountRef = useRef<HTMLDivElement | null>(null);

    chapterIdRef.current = chapterId;
    commentsRef.current = chapterComments;
    storedContentRef.current = chapterContent;
    onDraftFlushRef.current = onDraftFlush;
    onTypingRef.current = onTyping;
    onCommentSelectRef.current = onCommentSelect;

    /** Stable fingerprint so late-arriving chapter body re-triggers hydrate without object-identity churn. */
    const chapterContentKey = serializeChapterContent(chapterContent);

    const flushDraft = useCallback(() => {
      if (flushTimerRef.current) {
        window.clearTimeout(flushTimerRef.current);
        flushTimerRef.current = null;
      }
      if (!needsFlushRef.current) return;

      const editor = editorRef.current;
      if (!editor || editor.isDestroyed) return;
      if (syncedChapterIdRef.current !== chapterIdRef.current) return;

      needsFlushRef.current = false;
      const nextContent = manuscriptJsonFromEditor(editor);
      const syncedComments = syncChapterCommentsAnchorsFromEditor(editor, commentsRef.current);
      const storedContent = normalizeStoredContent(storedContentRef.current);
      const contentChanged = chapterContentBodyChanged(storedContent, nextContent);
      const commentsChanged = syncedComments !== commentsRef.current;
      if (!contentChanged && !commentsChanged) return;

      onDraftFlushRef.current({
        chapterId: chapterIdRef.current,
        content: contentChanged ? nextContent : storedContent,
        comments: syncedComments,
      });
      if (contentChanged) {
        baselineContentRef.current = nextContent;
      }
    }, []);

    const scheduleFlush = useCallback(() => {
      needsFlushRef.current = true;
      onTypingRef.current?.();
      if (flushTimerRef.current) {
        window.clearTimeout(flushTimerRef.current);
      }
      flushTimerRef.current = window.setTimeout(() => {
        flushTimerRef.current = null;
        flushDraft();
      }, flushDebounceMs);
    }, [flushDraft, flushDebounceMs]);

    const editor = useEditor({
      immediatelyRender: true,
      shouldRerenderOnTransaction: false,
      extensions: buildManuscriptExtensions(liveCollabYDoc, liveCollabAwareness, liveCollabUser, resolveImageSrc),
      content: chapterToTipTapDoc(chapterContent),
      editorProps: {
        attributes: {
          class: "manuscript-prose",
          "data-placeholder": "Start writing your chapter...",
          autocorrect: "off",
          ...(liveCollabYDoc ? { "data-live-collab": liveCollabStatus || "on" } : {}),
        },
        handleClick(view, pos) {
          const commentId = getChapterCommentIdAtOverlayPos(view.state.doc, view.state.schema, pos);
          onCommentSelectRef.current(commentId);
          return false;
        },
      },
      onCreate: ({ editor: created }) => {
        // Ensure first paint isn't an empty doc when chapter body was available at mount.
        const plain = chapterContentToPlainText(storedContentRef.current).trim();
        if (created.state.doc.textContent.trim().length === 0 && plain.length > 0) {
          hydrateManuscriptEditor(
            created,
            { content: storedContentRef.current, comments: commentsRef.current },
            appliedCommentMarkIdsRef.current
          );
        }
        syncedChapterIdRef.current = chapterIdRef.current;
        syncedEditorRef.current = created;
        baselineContentRef.current = serializeChapterContent(storedContentRef.current);
      },
      onUpdate: () => {
        scheduleFlush();
      },
      onSelectionUpdate: () => {
        // Toolbar B/I/U state — selection only, not every keystroke.
        setFormatTick((n) => n + 1);
      },
    });

    editorRef.current = editor;

    const performUndo = useCallback(() => {
      const editor = editorRef.current;
      if (!editor) return false;
      if (editor.can().undo()) {
        editor.chain().focus().undo().run();
        return true;
      }
      const current = manuscriptJsonFromEditor(editor);
      const snap = documentUndoRef.current.undo(current);
      if (!snap) return false;
      hydrateManuscriptEditor(
        editor,
        { content: snap.payload, comments: commentsRef.current },
        appliedCommentMarkIdsRef.current
      );
      scheduleFlush();
      return true;
    }, [scheduleFlush]);

    const performRedo = useCallback(() => {
      const editor = editorRef.current;
      if (!editor) return false;
      if (editor.can().redo()) {
        editor.chain().focus().redo().run();
        return true;
      }
      const current = manuscriptJsonFromEditor(editor);
      const snap = documentUndoRef.current.redo(current);
      if (!snap) return false;
      hydrateManuscriptEditor(
        editor,
        { content: snap.payload, comments: commentsRef.current },
        appliedCommentMarkIdsRef.current
      );
      scheduleFlush();
      return true;
    }, [scheduleFlush]);

    useImperativeHandle(
      ref,
      () => ({
        getEditor: () => editorRef.current,
        flushDraft,
        hasPendingDraft: () => needsFlushRef.current,
        undo: performUndo,
        redo: performRedo,
      }),
      [flushDraft, performUndo, performRedo]
    );

    useEffect(() => {
      if (!editor || !chapterId) return;

      const editorRemounted = syncedEditorRef.current !== editor;
      if (editorRemounted) {
        syncedChapterIdRef.current = null;
      }

      const hadSyncedChapter = syncedChapterIdRef.current === chapterId;
      const reloadBumped = hadSyncedChapter && prevReloadTokenRef.current !== reloadToken;

      const incomingContent = normalizeStoredContent(storedContentRef.current);
      const incomingJson = serializeChapterContent(incomingContent);
      const editorJson = manuscriptJsonFromEditor(editor);
      const baselineJson = baselineContentRef.current;
      const hasUnsavedEdits = needsFlushRef.current || editorJson !== baselineJson;
      const editorEmpty = editor.state.doc.textContent.trim().length === 0;
      const incomingHasBody = chapterContentToPlainText(incomingContent).trim().length > 0;
      const needsEmptyRecovery = editorEmpty && incomingHasBody;
      const externalContentChanged =
        hadSyncedChapter && !reloadBumped && incomingJson !== baselineJson && !hasUnsavedEdits;

      if (hadSyncedChapter && !reloadBumped && !needsEmptyRecovery && !externalContentChanged) {
        return;
      }

      prevReloadTokenRef.current = reloadToken;

      if (reloadBumped && incomingJson === baselineJson) {
        const reconciled = reconcileManuscriptEditorComments(
          editor,
          commentsRef.current,
          appliedCommentMarkIdsRef.current
        );
        if (reconciled !== commentsRef.current) {
          onDraftFlushRef.current({
            chapterId,
            content: incomingContent,
            comments: reconciled,
          });
        }
        syncedEditorRef.current = editor;
        return;
      }

      if (reloadBumped && hasUnsavedEdits) {
        syncedEditorRef.current = editor;
        return;
      }

      if (reloadBumped) {
        documentUndoRef.current.push(editorJson);
      } else if (editorRemounted || !hadSyncedChapter) {
        documentUndoRef.current.clear();
      }

      const reconciled = hydrateManuscriptEditor(
        editor,
        { content: incomingContent, comments: commentsRef.current },
        appliedCommentMarkIdsRef.current
      );
      syncedChapterIdRef.current = chapterId;
      syncedEditorRef.current = editor;
      baselineContentRef.current = incomingJson;
      needsFlushRef.current = false;
      if (reconciled !== commentsRef.current) {
        onDraftFlushRef.current({
          chapterId,
          content: incomingContent,
          comments: reconciled,
        });
      }
    }, [editor, chapterId, reloadToken, chapterContentKey]);

    // After paint: if TipTap is still empty while chapter body exists, force hydrate once more.
    useLayoutEffect(() => {
      if (!editor || !chapterId) return;
      const plain = chapterContentToPlainText(storedContentRef.current).trim();
      if (!plain) return;
      if (editor.state.doc.textContent.trim().length > 0) return;
      hydrateManuscriptEditor(
        editor,
        { content: storedContentRef.current, comments: commentsRef.current },
        appliedCommentMarkIdsRef.current
      );
      syncedChapterIdRef.current = chapterId;
      syncedEditorRef.current = editor;
      baselineContentRef.current = serializeChapterContent(storedContentRef.current);
      needsFlushRef.current = false;
    }, [editor, chapterId, reloadToken, chapterContentKey]);

    /**
     * TipTap's <EditorContent> only attaches the view when `view.dom.parentNode` is set.
     * In the Tauri WKWebView that check can fail, leaving a permanently empty mount.
     * Attach the ProseMirror DOM ourselves into a real in-document host.
     */
    useLayoutEffect(() => {
      if (!editor || editor.isDestroyed) {
        setPlainFallback(false);
        return;
      }
      const host = editorMountRef.current;
      if (!host) return;

      try {
        const dom = editor.view.dom;
        if (!host.contains(dom)) {
          host.replaceChildren();
          host.appendChild(dom);
          editor.setOptions({ element: host });
        }
      } catch (error) {
        console.warn("[manuscript] failed to attach editor view", error);
        setPlainFallback(true);
        return;
      }

      const plain = chapterContentToPlainText(storedContentRef.current).trim();
      if (editor.state.doc.textContent.trim().length === 0 && plain.length > 0) {
        hydrateManuscriptEditor(
          editor,
          { content: storedContentRef.current, comments: commentsRef.current },
          appliedCommentMarkIdsRef.current
        );
      }

      const stillEmpty = editor.state.doc.textContent.trim().length === 0 && plain.length > 0;
      setPlainFallback(stillEmpty);
      if (!stillEmpty) {
        syncedChapterIdRef.current = chapterId;
        syncedEditorRef.current = editor;
        baselineContentRef.current = serializeChapterContent(storedContentRef.current);
      }
    }, [editor, chapterId, reloadToken, chapterContentKey]);

    useEffect(() => {
      if (!editor) return;
      if (themePreset && prevThemeRef.current !== themePreset) {
        appliedCommentMarkIdsRef.current.clear();
        prevThemeRef.current = themePreset;
      }
      applyMarksFromChapterComments(editor, chapterComments, appliedCommentMarkIdsRef.current);
    }, [editor, chapterComments, themePreset]);

    useEffect(() => {
      if (!editor) return;
      const root = editor.view.dom;
      root.querySelectorAll("[data-comment-id], [data-chapter-comment-id]").forEach((el) => {
        const id =
          el.getAttribute("data-comment-id") ?? el.getAttribute("data-chapter-comment-id");
        el.classList.toggle("is-selected", id != null && id === selectedCommentId);
      });
    }, [editor, selectedCommentId]);

    useEffect(() => {
      editor?.setEditable(editable);
    }, [editor, editable]);

    // This pane is keyed by chapter id, so switching chapters unmounts it.
    // Clearing the timer without flushing would silently drop whatever was typed
    // in the last few hundred milliseconds. Layout-effect cleanup runs before
    // TipTap's passive cleanup destroys the editor we need to read from.
    useLayoutEffect(() => {
      return () => {
        try {
          flushDraft();
        } catch {
          // A destroyed editor cannot be read; the timer cleanup below still runs.
        }
        if (flushTimerRef.current) {
          window.clearTimeout(flushTimerRef.current);
        }
      };
    }, [flushDraft]);

    useEffect(() => {
      const onKey = (event: KeyboardEvent) => {
        if (!(event.metaKey || event.ctrlKey)) return;
        if (event.shiftKey || event.altKey) return;
        if (event.key.toLowerCase() !== "f") return;
        const target = event.target as Node | null;
        const root = rootRef.current;
        if (!root || !target || !root.contains(target)) return;
        event.preventDefault();
        setFindReplaceOpen(true);
      };
      window.addEventListener("keydown", onKey, true);
      return () => window.removeEventListener("keydown", onKey, true);
    }, [setFindReplaceOpen]);

    const plainBody = chapterContentToPlainText(chapterContent);
    const showPlain = plainFallback || !editor;

    if (!editor && !plainBody.trim()) return null;

    const formatDisabled = !editable || !editor;
    const formatToolbar = editor ? (
      <div className="manuscript-format-toolbar" role="toolbar" aria-label="Text formatting">
        <button
          type="button"
          className="manuscript-format-toolbar__btn"
          title="Bold"
          aria-label="Bold"
          aria-pressed={editor.isActive("bold")}
          disabled={formatDisabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => editor.chain().focus().toggleBold().run()}
        >
          <b>B</b>
        </button>
        <button
          type="button"
          className="manuscript-format-toolbar__btn"
          title="Italic"
          aria-label="Italic"
          aria-pressed={editor.isActive("italic")}
          disabled={formatDisabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => editor.chain().focus().toggleItalic().run()}
        >
          <i>I</i>
        </button>
        <button
          type="button"
          className="manuscript-format-toolbar__btn"
          title="Underline"
          aria-label="Underline"
          aria-pressed={editor.isActive("underline")}
          disabled={formatDisabled}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => editor.chain().focus().toggleUnderline().run()}
        >
          <u>U</u>
        </button>
        <button
          type="button"
          className="manuscript-format-toolbar__btn"
          title="Mark as proper noun (do not translate)"
          aria-label="Mark as proper noun"
          aria-pressed={editor.isActive("properNoun")}
          disabled={formatDisabled || editor.state.selection.empty}
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => editor.chain().focus().toggleProperNoun().run()}
        >
          Aa
        </button>
        {onRequestComment ? (
          <button
            type="button"
            className="manuscript-format-toolbar__btn"
            title="Comment on selection"
            aria-label="Comment on selection"
            disabled={formatDisabled || editor.state.selection.empty}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => onRequestComment()}
          >
            Comment
          </button>
        ) : null}
        {allowCitations && citationOptions.length > 0 ? (
          <label className="manuscript-format-toolbar__btn">
            <span className="sr-only">Insert citation</span>
            <select
              aria-label="Insert citation"
              disabled={formatDisabled}
              defaultValue=""
              onMouseDown={(event) => event.preventDefault()}
              onChange={(event) => {
                const id = event.target.value;
                event.target.value = "";
                if (id) onInsertCitation?.(id);
              }}
            >
              <option value="">Cite…</option>
              {citationOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.label}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        {allowImages && imageOptions.length > 0 ? (
          <label className="manuscript-format-toolbar__btn">
            <span className="sr-only">Insert image</span>
            <select
              aria-label="Insert image from research"
              disabled={formatDisabled}
              defaultValue=""
              onMouseDown={(event) => event.preventDefault()}
              onChange={(event) => {
                const id = event.target.value;
                event.target.value = "";
                if (id) onInsertImage?.(id);
              }}
            >
              <option value="">Image…</option>
              {imageOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>
                  {opt.title}
                </option>
              ))}
            </select>
          </label>
        ) : null}
      </div>
    ) : null;

    return (
      <div className="manuscript-editor-root" ref={rootRef}>
        {formatToolbarHost && formatToolbar && !showPlain
          ? createPortal(formatToolbar, formatToolbarHost)
          : null}
        {findReplaceOpen && editor && !showPlain ? (
          <FindReplaceBar
            editor={editor}
            open={findReplaceOpen}
            onClose={() => setFindReplaceOpen(false)}
          />
        ) : null}
        {/* Keep TipTap host mounted (even when hidden) so the view can attach in WKWebView. */}
        <div
          ref={editorMountRef}
          className="manuscript-editor-content"
          data-manuscript-mount="true"
          hidden={showPlain}
          aria-hidden={showPlain}
        />
        {showPlain ? (
          <textarea
            className="manuscript-editor-content manuscript-prose manuscript-plain-fallback"
            aria-label="Chapter manuscript"
            value={plainBody}
            readOnly={!editable}
            onChange={(event) => {
              const next = event.target.value;
              onDraftFlushRef.current({
                chapterId,
                content: next,
                comments: commentsRef.current,
              });
              baselineContentRef.current = serializeChapterContent(next);
              needsFlushRef.current = false;
            }}
          />
        ) : null}
      </div>
    );
  })
);

function normalizeStoredContent(value: unknown): string {
  return serializeChapterContent(value);
}
